import { Box3, Raycaster, Vector2, Vector3, type Camera, type Object3D } from 'three';
import { getHeroId, isInteractive } from '../scene/tagging';
import type { PointerEventName } from './actions';

// Scratch reused by the screen-bbox snap (no per-click allocation).
const SNAP_BOX = new Box3();
const SNAP_CORNER = new Vector3();

/** True only if `obj` and every ancestor are `visible` — i.e. it's actually
 *  rendered. three.js raycasting ignores `visible`, so picking checks it itself
 *  to stay WYSIWYG (item 7): an off-era hero hidden by the crossfade (its meshes
 *  set `visible=false`) must not remain clickable. Walks to the scene root. */
function isRenderChainVisible(obj: Object3D): boolean {
  let n: Object3D | null = obj;
  while (n) {
    if (!n.visible) return false;
    n = n.parent;
  }
  return true;
}

export interface PointerEventInfo {
  object: Object3D;
  heroId?: string;
  /** Viewport coords of the originating click (set on `click` only) — lets a
   *  demo surface place a transient chip at the cursor from the same event,
   *  with no second DOM listener (and no listener-order race for the coords). */
  clientX?: number;
  clientY?: number;
}

export type PointerEventHandler = (info: PointerEventInfo) => void;

export class PointerInteraction {
  private raycaster = new Raycaster();
  private pointer = new Vector2();
  private hovered: Object3D | null = null;
  // PF5: restrict the per-move raycast to the interactive subtrees instead of
  // the whole content root. The photoscan room is a huge non-interactive mesh
  // that every pointermove was ray-testing for nothing; casting only the
  // tagged subtrees skips it. null = not yet built → fall back to the full
  // root cast (original behavior), so this is safe even if never refreshed.
  private interactiveRoots: Object3D[] | null = null;
  // A1 — a click whose pointer travelled past this slop was a DRAG (rail
  // touch-drag, cloth grab, freeform orbit), not a tap: suppress the hero-select
  // so dragging never selects. Recorded on pointerdown, checked on click.
  private downX = 0;
  private downY = 0;
  private readonly CLICK_SLOP_PX = 6;

  private listeners: Record<PointerEventName, PointerEventHandler[]> = {
    click: [],
    hoverIn: [],
    hoverOut: [],
  };
  // Click-away: a tap that hit NO interactive hero. Kept off the PointerEventName
  // map since it carries no hero payload and no interaction rule matches it — it
  // exists only so the highlighter can deselect on an empty-space click.
  private emptyClickListeners: Array<() => void> = [];

  constructor(
    private domElement: HTMLElement,
    private camera: Camera,
    private root: Object3D,
  ) {}

  /** Subscribe. Returns an unsubscribe function. */
  on(event: PointerEventName, handler: PointerEventHandler): () => void {
    this.listeners[event].push(handler);
    return () => {
      const arr = this.listeners[event];
      const i = arr.indexOf(handler);
      if (i >= 0) arr.splice(i, 1);
    };
  }

  /** Subscribe to empty-space clicks (a tap that hit no interactive hero).
   *  Returns an unsubscribe. */
  onEmptyClick(handler: () => void): () => void {
    this.emptyClickListeners.push(handler);
    return () => {
      const i = this.emptyClickListeners.indexOf(handler);
      if (i >= 0) this.emptyClickListeners.splice(i, 1);
    };
  }

  attach(): void {
    this.domElement.addEventListener('pointerdown', this.handleDown);
    this.domElement.addEventListener('pointermove', this.handleMove);
    this.domElement.addEventListener('click', this.handleClick);
  }

  detach(): void {
    this.domElement.removeEventListener('pointerdown', this.handleDown);
    this.domElement.removeEventListener('pointermove', this.handleMove);
    this.domElement.removeEventListener('click', this.handleClick);
  }

  private setPointerFromEvent(e: PointerEvent | MouseEvent) {
    const rect = this.domElement.getBoundingClientRect();
    this.pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  }

  /** Rebuild the restricted cast set from the current root: every
   *  `interactive`-tagged subtree (heroes + cloth), excluding the photoscan and
   *  other static dressing. Call after the scene's interactive objects are in
   *  place, and again if they change. Until called, the cast falls back to the
   *  whole root. */
  refreshInteractiveRoots(): void {
    const roots: Object3D[] = [];
    this.root.traverse((o) => {
      if (isInteractive(o)) roots.push(o);
    });
    this.interactiveRoots = roots;
  }

  private firstInteractiveHit(skipCloth = false): Object3D | null {
    this.raycaster.setFromCamera(this.pointer, this.camera);
    // Cast the restricted interactive set when built; else the whole root.
    // The ancestor-walk below still resolves a child-mesh hit up to its tagged
    // interactive root either way.
    const hits = this.interactiveRoots
      ? this.raycaster.intersectObjects(this.interactiveRoots, true)
      : this.raycaster.intersectObject(this.root, true);
    for (const hit of hits) {
      // WYSIWYG picking (item 7): three.js raycasts ignore the `visible` flag,
      // but the era crossfade hides off-era heroes via mesh `visible=false`
      // (opacityCrossfade) — so without this an invisible past/present hero
      // stays clickable in the wrong era. Skip any hit whose render chain is
      // hidden: only what's actually drawn is pickable. (A mid-crossfade ghost
      // — both eras momentarily semi-visible — is parked; this gates the binary
      // flag, which is what era visibility resolves to once settled.)
      if (!isRenderChainVisible(hit.object)) continue;
      let obj: Object3D | null = hit.object;
      while (obj) {
        if (isInteractive(obj)) {
          // Cloth pass-through for SELECT clicks: the cloth is GRAB-only
          // (ClothGrabController raycasts it itself on pointerdown), but it's
          // tagged `interactive` so it also lands in this pick — and draped over
          // the table it sits IN FRONT of the chair, so a click on the chair was
          // resolving to the cloth and the chair never fired. On a click we skip
          // the cloth hit and let the ray fall through to the hero behind it.
          // Hover keeps the cloth (skipCloth=false) so the grab cursor still shows.
          if (skipCloth && obj.userData.is_cloth) break;
          return obj;
        }
        obj = obj.parent;
      }
    }
    return null;
  }

  /** Forgiving fallback for a click that hit NO interactive mesh: snap to the
   *  nearest interactive hero whose projected screen bounding-box contains the
   *  click. Sparse/gappy meshes (the molded chair) slip the exact ray through
   *  their gaps, and the chair's backrest can't be a solid click-proxy without
   *  occluding the cassettes behind it — so instead, a MISS inside a hero's
   *  silhouette rect resolves to that hero. Only consulted on a miss, so precise
   *  hits (and the small cassettes) are never overridden. Cloth is skipped (it's
   *  grab-only). When several rects contain the click, the SMALLEST (most specific)
   *  wins — so a click on the chair's tall see-through backrest, where only the
   *  chair's box contains it, snaps to the chair; while a near-miss beside a small
   *  cassette still snaps to the cassette rather than the big chair box around it.
   *  (Nearest-centre was wrong: the chair's box centre sits low at the legs, so a
   *  high backrest click fell to a small hero behind it.) */
  private snapToHero(): Object3D | null {
    const roots = this.interactiveRoots ?? [this.root];
    const cx = this.pointer.x, cy = this.pointer.y;
    let best: Object3D | null = null;
    let bestArea = Infinity;
    for (const root of roots) {
      if (root.userData.is_cloth || !isRenderChainVisible(root)) continue;
      SNAP_BOX.setFromObject(root);
      if (SNAP_BOX.isEmpty()) continue;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, anyFront = false;
      for (let i = 0; i < 8; i++) {
        SNAP_CORNER.set(i & 1 ? SNAP_BOX.max.x : SNAP_BOX.min.x, i & 2 ? SNAP_BOX.max.y : SNAP_BOX.min.y, i & 4 ? SNAP_BOX.max.z : SNAP_BOX.min.z);
        SNAP_CORNER.project(this.camera);
        if (SNAP_CORNER.z < 1) anyFront = true;
        if (SNAP_CORNER.x < minX) minX = SNAP_CORNER.x;
        if (SNAP_CORNER.x > maxX) maxX = SNAP_CORNER.x;
        if (SNAP_CORNER.y < minY) minY = SNAP_CORNER.y;
        if (SNAP_CORNER.y > maxY) maxY = SNAP_CORNER.y;
      }
      if (!anyFront || cx < minX || cx > maxX || cy < minY || cy > maxY) continue;
      const area = (maxX - minX) * (maxY - minY);
      if (area < bestArea) { bestArea = area; best = root; }
    }
    return best;
  }

  private emit(event: PointerEventName, info: PointerEventInfo) {
    // Resilient dispatch: the click listeners are independent subsystems (rules
    // engine, highlighter, demo chip, the story director — registered LAST). A
    // throw in any one must not break the loop and silently kill the rest; the
    // story director going dead (no sit pill, no cassette progress) while phase
    // still advances via the view-poll is exactly that failure. Iterate a copy so
    // a listener that (un)subscribes mid-dispatch can't corrupt the walk.
    for (const fn of [...this.listeners[event]]) {
      try { fn(info); }
      catch (e) { console.error(`[pointer] ${event} listener threw — continuing`, e); }
    }
  }

  private handleMove = (e: PointerEvent) => {
    this.setPointerFromEvent(e);
    const hit = this.firstInteractiveHit();
    if (hit !== this.hovered) {
      if (this.hovered) {
        this.emit('hoverOut', { object: this.hovered, heroId: getHeroId(this.hovered) });
      }
      this.hovered = hit;
      // Cloth advertises that it's grabbable, not merely clickable: a `grab`
      // cursor over an `is_cloth` mesh, the usual `pointer` over everything
      // else interactive.
      this.domElement.style.cursor = hit
        ? (hit.userData.is_cloth ? 'grab' : 'pointer')
        : 'default';
      if (hit) {
        this.emit('hoverIn', { object: hit, heroId: getHeroId(hit) });
      }
    }
  };

  private handleDown = (e: PointerEvent) => {
    this.downX = e.clientX;
    this.downY = e.clientY;
  };

  private handleClick = (e: MouseEvent) => {
    // A1 — a click whose pointer travelled is a DRAG (rail touch-drag, cloth
    // grab, freeform orbit), not a tap: the browser still fires 'click' on
    // release, but selecting a hero then is wrong. A tap stays under the slop.
    if (Math.hypot(e.clientX - this.downX, e.clientY - this.downY) > this.CLICK_SLOP_PX) return;
    this.setPointerFromEvent(e);
    // Exact pick first (skip cloth → falls through to the hero behind it); on a
    // total miss, snap to the nearest hero silhouette so a sparse mesh (the chair)
    // is still selectable through its gaps without an occluding proxy.
    const hit = this.firstInteractiveHit(true) ?? this.snapToHero();
    if (hit) this.emit('click', { object: hit, heroId: getHeroId(hit), clientX: e.clientX, clientY: e.clientY });
    else for (const fn of this.emptyClickListeners) fn(); // click-away → deselect (dev highlight)
  };
}
